import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi } from '@/api/errors';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import type { AdminUserViewModel } from '../api';

export function DeleteUserConfirmDialog({
  user,
  onCancel,
  onConfirm,
  pending,
  submitError,
}: {
  readonly user: AdminUserViewModel | null;
  readonly onCancel: () => void;
  readonly onConfirm: (user: AdminUserViewModel) => void;
  readonly pending: boolean;
  readonly submitError: unknown;
}): ReactElement {
  const { t } = useTranslation();
  const open = user !== null;
  const name =
    user !== null
      ? user.name.length > 0
        ? user.name
        : user.email.length > 0
        ? user.email
        : t('routes.admin.users.unknownUser')
      : '';
  const submitErrorCode = getErrorCodeForUi(submitError);

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onCancel(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('routes.admin.users.delete.title')}</DialogTitle>
          <DialogDescription>
            {t('routes.admin.users.delete.message', { name })}
          </DialogDescription>
        </DialogHeader>
        {submitError !== undefined && submitError !== null && (
          <p role="alert" className="text-sm text-red-600">
            {t('routes.admin.users.delete.errors.submit', { code: submitErrorCode })}
          </p>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
            {t('routes.admin.users.delete.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            loading={pending}
            onClick={() => {
              if (user !== null) onConfirm(user);
            }}
          >
            {t('routes.admin.users.delete.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
